import {
  searchNylasBillableAccounts,
  scrollNylasBillableAccounts
} from '@services/query-services/nylas.query-service';

/**
 * Merges bot usage data with usage stats data by the "when" field.
 * Both data sources return monthly rollups with matching "when" timestamps.
 *
 * @param {Object|Array} botsUsagesData - Response from /recall/bots/usages endpoint
 * @param {Object|Array} usagesStatsData - Response from /usages/stats endpoint
 * @returns {Array} Merged and sorted array of usage data (most recent first)
 */
export function mergeBotsAndStatsData(botsUsagesData, usagesStatsData) {
  const botsData = botsUsagesData?.data || botsUsagesData || [];
  const statsData = usagesStatsData?.data || usagesStatsData || [];

  // Create a map of stats data by "when"
  const statsMap = new Map();
  statsData.forEach((item) => {
    statsMap.set(item.when, item);
  });

  // Merge bots data with stats data
  const merged = botsData.map((botItem) => {
    const statsItem = statsMap.get(botItem.when) || {};
    return {
      when: botItem.when,
      duration: botItem.duration || 0,
      count: botItem.count || 0,
      bytes_of_media: botItem.bytes_of_media || 0,
      total_tokens: statsItem.total_tokens || 0
    };
  });

  // Add any stats entries that don't have matching bots data
  statsData.forEach((statsItem) => {
    const exists = merged.some((item) => item.when === statsItem.when);
    if (!exists) {
      merged.push({
        when: statsItem.when,
        duration: 0,
        count: 0,
        bytes_of_media: 0,
        total_tokens: statsItem.total_tokens || 0
      });
    }
  });

  // Sort by month descending (most recent first)
  return merged.sort((a, b) => b.when - a.when);
}

/**
 * Fetches all billable users using scroll pagination and downloads as CSV.
 *
 * @param {string|number} month - Month number (1-12)
 * @param {string|number} year - Year (e.g., 2026)
 */
export async function downloadNylasBillableUsersCSV(month, year) {
  // Initial request with scroll parameter
  const initialRes = await searchNylasBillableAccounts({
    month,
    year,
    scroll: '30s',
    includeTotal: true
  });

  const allUsers = [...(initialRes?.data || [])];
  const total = initialRes?.total || 0;
  let scrollId = initialRes?.scrollId;

  // Continue scrolling until we have all records
  while (allUsers.length < total && scrollId) {
    const scrollRes = await scrollNylasBillableAccounts(scrollId);
    const pageData = scrollRes?.data || [];
    allUsers.push(...pageData);
    scrollId = scrollRes?.scrollId;
  }

  // Generate CSV
  const headers = ['Name', 'Email', 'Provider'];
  const rows = allUsers.map((user) => [
    user.person?.name || '',
    user.email_address || '',
    user.provider || ''
  ]);

  const csvContent = [
    headers.join(','),
    ...rows.map((row) => row.map((cell) => `"${cell}"`).join(','))
  ].join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = `nylas-billable-users-${month}-${year}.csv`;
  link.click();
  URL.revokeObjectURL(link.href);
}
